import React, { useState, useRef, useEffect } from 'react';
import { X, GripHorizontal, ChevronsLeft, ChevronsRight, Rewind, Square, Play, Pause, Circle, FastForward, Repeat } from 'lucide-react';

/**
 * Floating, draggable, closable transport panel for the DAW.
 *
 * - Drag by the header handle to move it anywhere on screen
 * - Close button hides it (parent controls the `visible` state)
 * - Position persists in localStorage so it stays where you last put it
 */
const FloatingTransport = ({
  visible,
  onClose,
  isPlaying,
  isRecording,
  onPlay,
  onStop,
  onRecord,
  onRewind,
  onFastForward,
  onGoToStart,
  onGoToEnd,
  loopEnabled,
  onToggleLoop,
  currentBeatLabel,
  currentTimeLabel,
  tempo,
  onTempoChange,
}) => {
  const [pos, setPos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('daw_floating_transport_pos') || 'null');
      if (saved && typeof saved.x === 'number' && typeof saved.y === 'number') return saved;
    } catch (e) { /* ignore */ }
    return { x: 320, y: 120 };
  });
  const draggingRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem('daw_floating_transport_pos', JSON.stringify(pos));
    } catch (e) { /* ignore */ }
  }, [pos]);

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current) return;
      const { startX, startY, startPosX, startPosY } = draggingRef.current;
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const nextX = Math.max(0, Math.min(window.innerWidth - 200, startPosX + (clientX - startX)));
      const nextY = Math.max(0, Math.min(window.innerHeight - 80, startPosY + (clientY - startY)));
      setPos({ x: nextX, y: nextY });
    };
    const onUp = () => { draggingRef.current = null; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, []);

  const startDrag = (e) => {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    draggingRef.current = {
      startX: clientX,
      startY: clientY,
      startPosX: pos.x,
      startPosY: pos.y,
    };
  };

  if (!visible) return null;

  return (
    <div
      ref={panelRef}
      className="fixed z-40 bg-[#181818]/95 backdrop-blur-md border-2 border-amber-500/50 rounded-xl shadow-2xl"
      style={{ left: pos.x, top: pos.y, minWidth: 720 }}
      data-testid="floating-transport"
    >
      {/* Drag handle header */}
      <div
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        className="flex items-center justify-between px-3 py-2 border-b border-amber-500/30 cursor-grab active:cursor-grabbing bg-black/40 rounded-t-xl"
      >
        <div className="flex items-center gap-2">
          <GripHorizontal className="w-5 h-5 text-amber-400" />
          <span className="text-sm font-black tracking-widest text-amber-400">FLOATING TRANSPORT · DRAG TO MOVE</span>
        </div>
        <button
          onClick={onClose}
          className="w-8 h-8 rounded-md hover:bg-red-500/30 flex items-center justify-center"
          title="Close (you can reopen from the toolbar)"
          data-testid="floating-transport-close"
        >
          <X className="w-5 h-5 text-gray-300" />
        </button>
      </div>

      {/* Transport controls */}
      <div className="p-4 flex flex-col items-center gap-3">
        <div className="flex items-center gap-3">
          <button onClick={onGoToStart} className="w-14 h-14 bg-[#2a2a2a] hover:bg-[#3a3a3a] rounded-lg flex items-center justify-center" title="Go to Start">
            <ChevronsLeft className="w-7 h-7" />
          </button>
          <button onClick={onRewind} className="w-14 h-14 bg-[#2a2a2a] hover:bg-[#3a3a3a] rounded-lg flex items-center justify-center" title="Rewind">
            <Rewind className="w-7 h-7" />
          </button>
          <button onClick={onStop} className="w-14 h-14 bg-[#2a2a2a] hover:bg-[#3a3a3a] rounded-lg flex items-center justify-center" title="Stop">
            <Square className="w-7 h-7" />
          </button>
          <button
            onClick={onPlay}
            data-testid="floating-transport-play"
            className={`w-20 h-14 rounded-lg flex items-center justify-center shadow-lg ${
              isPlaying ? 'bg-amber-500 hover:bg-amber-400 text-black' : 'bg-green-600 hover:bg-green-500 text-white'
            }`}
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <Pause className="w-9 h-9" /> : <Play className="w-9 h-9" />}
          </button>
          <button
            onClick={onRecord}
            className={`w-14 h-14 rounded-lg flex items-center justify-center ${
              isRecording ? 'bg-red-500 animate-pulse' : 'bg-[#2a2a2a] hover:bg-red-600'
            }`}
            title={isRecording ? 'Stop Recording' : 'Record'}
          >
            <Circle className={`w-7 h-7 ${isRecording ? 'fill-current' : ''}`} />
          </button>
          <button onClick={onFastForward} className="w-14 h-14 bg-[#2a2a2a] hover:bg-[#3a3a3a] rounded-lg flex items-center justify-center" title="Fast Forward">
            <FastForward className="w-7 h-7" />
          </button>
          <button onClick={onGoToEnd} className="w-14 h-14 bg-[#2a2a2a] hover:bg-[#3a3a3a] rounded-lg flex items-center justify-center" title="Go to End">
            <ChevronsRight className="w-7 h-7" />
          </button>
          <button
            onClick={onToggleLoop}
            className={`w-14 h-14 rounded-lg flex items-center justify-center ${
              loopEnabled ? 'bg-blue-500 hover:bg-blue-400' : 'bg-[#2a2a2a] hover:bg-[#3a3a3a]'
            }`}
            title="Loop"
          >
            <Repeat className="w-7 h-7" />
          </button>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-black px-3 py-2 rounded font-mono text-base text-white">
            <span className="text-gray-400">{currentBeatLabel}</span>
            <span className="text-[#333] mx-2">|</span>
            <span>{currentTimeLabel}</span>
          </div>
          <div className="flex items-center gap-2 bg-black px-3 py-2 rounded">
            <span className="text-sm font-bold text-amber-400">BPM</span>
            <input
              type="number"
              value={tempo}
              onChange={(e) => onTempoChange(Math.max(20, Math.min(300, parseInt(e.target.value) || 120)))}
              className="w-16 bg-transparent text-lg font-bold text-center outline-none text-white"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloatingTransport;
